import React, { createContext, useState, useContext, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { mainAuth as auth } from './auth/appAuth';
import { AuthContext } from './context/AuthContext';
import { fetchCurrentUser } from './api/me';
import { tryGetApiToken } from './api/authToken';
import { patchCompany } from './api/company/company';
import {
  shouldDeferCompanyTheme,
  readPendingStartedAt,
  isPendingSecondFactorActive,
} from './features/two-factor-auth/utils/pendingSecondFactorSession';

const ThemeContext = createContext();
const DEFAULT_COLOR = '#1976d2';

function clampChannel(value) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function hexToRgb(hex) {
  const normalized = hex.replace('#', '');
  const isShort = normalized.length === 3 || normalized.length === 4;
  if (isShort) {
    const r = parseInt(normalized[0] + normalized[0], 16);
    const g = parseInt(normalized[1] + normalized[1], 16);
    const b = parseInt(normalized[2] + normalized[2], 16);
    return { r, g, b };
  }
  const r = parseInt(normalized.substring(0, 2), 16);
  const g = parseInt(normalized.substring(2, 4), 16);
  const b = parseInt(normalized.substring(4, 6), 16);
  return { r, g, b };
}

function rgbToHex(r, g, b) {
  const toHex = (c) => clampChannel(c).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function lighten(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const nr = r + (255 - r) * amount;
  const ng = g + (255 - g) * amount;
  const nb = b + (255 - b) * amount;
  return rgbToHex(nr, ng, nb);
}

function darken(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const nr = r * (1 - amount);
  const ng = g * (1 - amount);
  const nb = b * (1 - amount);
  return rgbToHex(nr, ng, nb);
}

function toRgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${clampChannel(r)}, ${clampChannel(g)}, ${clampChannel(b)}, ${alpha})`;
}

function getReadableTextColor(hex) {
  const { r, g, b } = hexToRgb(hex);
  const srgb = [r, g, b].map((c) => c / 255);
  const linear = srgb.map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
  const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

export const useTheme = () => useContext(ThemeContext);

export const CustomThemeProvider = ({ children }) => {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const authState = useContext(AuthContext);
  const companyData = authState?.companyData;
  const updateCompanyData = authState?.updateCompanyData;
  const companyIdRef = useRef(companyData?.id || null);

  const [primaryColor, setPrimaryColor] = useState(DEFAULT_COLOR);
  const [mode, setMode] = useState(() => localStorage.getItem('themeMode') || 'light');

  const deferGlobalTheme =
    shouldDeferCompanyTheme(location.pathname) ||
    isPendingSecondFactorActive() ||
    Boolean(readPendingStartedAt());

  useEffect(() => {
    if (companyData?.id) {
      companyIdRef.current = companyData.id;
    }
    if (companyData?.themeColor) {
      setPrimaryColor(companyData.themeColor);
    }
    if (companyData?.themeMode) {
      setMode(companyData.themeMode);
      localStorage.setItem('themeMode', companyData.themeMode);
    }
  }, [companyData?.id, companyData?.themeColor, companyData?.themeMode]);

  useEffect(() => {
    const isPublicStorefront =
      typeof window !== 'undefined' &&
      window.location &&
      window.location.pathname.includes('/book/');
    if (isPublicStorefront) {
      return undefined;
    }

    const fetchThemeSettings = async () => {
      if (shouldDeferCompanyTheme(pathnameRef.current)) {
        return;
      }

      try {
        const token = await tryGetApiToken();
        if (!token && !auth.currentUser) return;
        const payload = token ? await fetchCurrentUser(token) : null;
        const company = payload?.company || null;
        if (!company?.id) return;
        companyIdRef.current = company.id;
        if (company.themeColor) {
          setPrimaryColor(company.themeColor);
        }
        if (company.themeMode) {
          setMode(company.themeMode);
          localStorage.setItem('themeMode', company.themeMode);
        }
      } catch (error) {
        console.error('Error fetching theme settings:', error);
      }
    };

    fetchThemeSettings();
    const unsubscribeAuth = auth.onAuthStateChanged(fetchThemeSettings);
    const onThemeRefresh = () => {
      void fetchThemeSettings();
    };
    window.addEventListener('app-second-factor-complete', onThemeRefresh);
    window.addEventListener('app-theme-refresh', onThemeRefresh);
    return () => {
      unsubscribeAuth();
      window.removeEventListener('app-second-factor-complete', onThemeRefresh);
      window.removeEventListener('app-theme-refresh', onThemeRefresh);
    };
  }, []);

  useEffect(() => {
    if (deferGlobalTheme) {
      return;
    }
    document.documentElement.setAttribute('data-theme', mode);
    const root = document.documentElement;
    root.classList.toggle('force-light', mode !== 'dark');
    if (mode === 'dark') {
      root.style.setProperty('--calendar-bg', '#1e1e1e');
      root.style.setProperty('--text-primary', '#ffffff');
      root.style.setProperty('--text-secondary', '#b0b0b0');
      root.style.setProperty('--text-disabled', '#666');
      root.style.setProperty('--hover-bg', 'rgba(144, 202, 249, 0.08)');
      root.style.setProperty('--calendar-cell-bg', '#262626');
      root.style.setProperty('--disabled-bg', '#333333');
      root.style.setProperty('--border-color', 'rgba(255, 255, 255, 0.1)');
      root.style.setProperty('--calendar-shadow', '0 2px 8px rgba(0, 0, 0, 0.4)');
      root.style.setProperty('--sidebar-bg', '#262626');
      root.style.setProperty('--header-bg', '#262626');
    } else {
      root.style.setProperty('--calendar-bg', '#ffffff');
      root.style.setProperty('--text-primary', '#1a1a1a');
      root.style.setProperty('--text-secondary', '#666');
      root.style.setProperty('--text-disabled', '#bbb');
      root.style.setProperty('--hover-bg', '#f0f7ff');
      root.style.setProperty('--calendar-cell-bg', 'transparent');
      root.style.setProperty('--disabled-bg', '#f5f5f5');
      root.style.setProperty('--border-color', '#e0e0e0');
      root.style.setProperty('--calendar-shadow', '0 2px 4px rgba(0, 0, 0, 0.1)');
      root.style.setProperty('--sidebar-bg', '#f5f5f5');
      root.style.setProperty('--header-bg', '#f2f4f8');
    }
  }, [mode, deferGlobalTheme]);

  useEffect(() => {
    if (deferGlobalTheme) {
      return;
    }
    const root = document.documentElement;
    root.style.setProperty('--primary-color', primaryColor);
    const primaryLight = lighten(primaryColor, 0.22);
    const primaryDark = darken(primaryColor, 0.18);
    root.style.setProperty('--primary-light', primaryLight);
    root.style.setProperty('--primary-dark', primaryDark);

    const hoverAlpha = mode === 'dark' ? 0.12 : 0.08;
    root.style.setProperty('--hover-bg', toRgba(primaryColor, hoverAlpha));
    root.style.setProperty('--primary-soft', toRgba(primaryColor, mode === 'dark' ? 0.15 : 0.10));
    root.style.setProperty('--primary-softer', toRgba(primaryColor, mode === 'dark' ? 0.08 : 0.05));

    if (mode === 'dark') {
      root.style.setProperty('--border-primary', 'rgba(255, 255, 255, 0.12)');
      root.style.setProperty('--header-border', 'rgba(255, 255, 255, 0.10)');
    } else {
      root.style.setProperty('--border-primary', '#e5e7eb');
      root.style.setProperty('--header-border', 'rgba(0, 0, 0, 0.08)');
    }

    const { r, g, b } = hexToRgb(primaryColor);
    root.style.setProperty('--shadow-light', `0 2px 8px rgba(${r}, ${g}, ${b}, 0.12)`);
    root.style.setProperty('--shadow-medium', `0 4px 16px rgba(${r}, ${g}, ${b}, 0.18)`);
    root.style.setProperty('--primary-contrast-color', getReadableTextColor(primaryColor));
  }, [primaryColor, mode, deferGlobalTheme]);

  const toggleColorMode = useCallback(async () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);

    const companyId = companyIdRef.current || companyData?.id;
    if (!companyId) return;

    try {
      const company = await patchCompany({
        companyId,
        company: { themeMode: newMode },
      });
      updateCompanyData?.({ ...(companyData || {}), ...company });
    } catch (error) {
      if (error?.status !== 401 && error?.status !== 403) {
        console.error('Error updating theme mode:', error);
      }
    }
  }, [mode, companyData, updateCompanyData]);

  const updateThemeColor = useCallback(async (newColor) => {
    setPrimaryColor(newColor);
    const companyId = companyIdRef.current || companyData?.id;
    if (!companyId) return;

    try {
      const company = await patchCompany({
        companyId,
        company: { themeColor: newColor },
      });
      updateCompanyData?.({ ...(companyData || {}), ...company });
    } catch (error) {
      if (error?.status !== 401 && error?.status !== 403) {
        console.error('Error updating theme color:', error);
      }
    }
  }, [companyData, updateCompanyData]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: primaryColor },
          background: {
            default: mode === 'dark' ? '#121212' : '#ffffff',
            paper: mode === 'dark' ? '#1e1e1e' : '#ffffff',
          },
          text: {
            primary: mode === 'dark' ? '#ffffff' : '#1a1a1a',
            secondary: mode === 'dark' ? '#b0b0b0' : '#666666',
            disabled: mode === 'dark' ? '#666666' : '#bbbbbb',
          },
          action: {
            hover: mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : '#f0f7ff',
          },
          divider: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e0e0e0',
        },
        typography: {
          fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
        },
        transitions: {
          duration: {
            shortest: 150,
            shorter: 200,
            short: 250,
            standard: 300,
            complex: 375,
            enteringScreen: 225,
            leavingScreen: 195,
          },
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              '*': {
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              },
              body: { msOverflowStyle: 'none' },
            },
          },
        },
      }),
    [mode, primaryColor]
  );

  const contextValue = useMemo(
    () => ({ primaryColor, mode, updateThemeColor, toggleColorMode }),
    [primaryColor, mode, updateThemeColor, toggleColorMode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};
